import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Users, Heart, Shield, ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Load Stripe from environment variable
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "pk_test_51QdVvH054uKSbCvLkVrK5VmPxr4PFCfE9a3PEP2yJL8QeS1VnL5HVWDlYXO7lPO8YbnDbL7FUvPdlGOflXKuKSRD00bVXnB02R");

// Embedded payment form component
const CheckoutForm = ({ tier, formData, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) return;

    setLoading(true);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        toast.error(submitError.message);
        setLoading(false);
        return;
      }

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
      });

      if (error) {
        toast.error(error.message);
        setLoading(false);
        return;
      }

      if (paymentIntent && paymentIntent.status === "succeeded") {
        // Confirm subscription on backend
        const response = await axios.post(
          `${API}/subscription/confirm-embedded`,
          {
            payment_intent_id: paymentIntent.id,
            tier: tier.id,
            email: formData.email,
            name: formData.name,
            password: formData.password,
            customer_id: paymentIntent.customer,
          },
          { withCredentials: true }
        );

        toast.success("Welcome to the movement! 🎉");
        onSuccess();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Payment failed");
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <PaymentElement />
      <Button
        type="submit"
        disabled={!stripe || loading}
        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-full py-6 text-xl font-bold shadow-lg"
        data-testid="complete-payment-btn"
      >
        {loading ? "Processing..." : `Join for $${tier.price}/mo`}
      </Button>
      <p className="text-xs text-center text-muted-foreground">
        Secure payment powered by Stripe. Cancel anytime.
      </p>
    </form>
  );
};

const Landing_v3 = () => {
  const navigate = useNavigate();
  const [showSignup, setShowSignup] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [formData, setFormData] = useState({ name: "", email: "", password: "" });
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(false);

  const tiers = [
    {
      id: "essential",
      name: "Essential",
      price: 9.99,
      coverage: "Up to $150 per event",
      color: "from-blue-500 to-cyan-500",
    },
    {
      id: "standard",
      name: "Standard",
      price: 19.99,
      coverage: "Up to $300 per event",
      color: "from-purple-500 to-pink-500",
      featured: true,
    },
    {
      id: "plus",
      name: "Plus",
      price: 39.99,
      coverage: "Up to $600 per event",
      color: "from-orange-500 to-red-500",
    },
  ];

  const handleJoinMovement = async (tier) => {
    setSelectedTier(tier);
    setShowSignup(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.password) {
      toast.error("Please fill all fields");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/subscription/create-payment-intent`, {
        tier: selectedTier.id,
        email: formData.email,
        name: formData.name,
      });

      setClientSecret(response.data.clientSecret);
    } catch (error) {
      toast.error("Failed to initialize payment");
      setLoading(false);
    }
  };

  const handlePaymentSuccess = () => {
    navigate("/dashboard");
  };

  const stats = [
    { number: "10K+", label: "Members Strong" },
    { number: "$2M+", label: "Bills Covered" },
    { number: "50K+", label: "Lives Changed" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50">
      {/* Hero Section */}
      {!showSignup ? (
        <>
          {/* Navigation */}
          <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200">
            <div className="container mx-auto px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl flex items-center justify-center">
                  <Heart className="h-6 w-6 text-white" />
                </div>
                <span className="text-2xl font-heading font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  DueDay
                </span>
              </div>
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  onClick={() => navigate("/login")}
                  className="rounded-full"
                  data-testid="nav-login-btn"
                >
                  Sign In
                </Button>
                <Button
                  onClick={() => document.getElementById("join").scrollIntoView({ behavior: "smooth" })}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-full px-8"
                  data-testid="nav-join-btn"
                >
                  Join the Movement
                </Button>
              </div>
            </div>
          </nav>

          {/* Hero */}
          <section className="pt-32 pb-20 px-6">
            <div className="container mx-auto max-w-6xl text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 rounded-full mb-8">
                <Sparkles className="h-4 w-4 text-purple-600" />
                <span className="text-purple-600 font-semibold text-sm">
                  Join 10,000+ members protecting each other
                </span>
              </div>

              <h1 className="text-5xl md:text-7xl font-heading font-black text-slate-900 leading-tight mb-6">
                We're Not a Service.
                <br />
                <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  We're a Movement.
                </span>
              </h1>

              <p className="text-2xl text-slate-600 mb-8 max-w-3xl mx-auto leading-relaxed">
                Thousands of people, pooling together to make sure no one falls behind on rent, utilities, or essentials.{" "}
                <span className="font-bold text-slate-900">We pay your bills. Right now. Together.</span>
              </p>

              <Button
                onClick={() => document.getElementById("join").scrollIntoView({ behavior: "smooth" })}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-full px-12 py-7 text-2xl font-bold shadow-2xl hover:scale-105 transition-transform"
                data-testid="hero-join-btn"
              >
                Join Us Now <ArrowRight className="ml-2 h-6 w-6" />
              </Button>

              {/* Stats */}
              <div className="mt-16 grid grid-cols-3 gap-8 max-w-3xl mx-auto">
                {stats.map((stat, idx) => (
                  <div key={idx} className="text-center">
                    <div className="text-4xl font-heading font-black bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
                      {stat.number}
                    </div>
                    <div className="text-sm text-slate-600 font-medium">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* How It Works - Movement Style */}
          <section className="py-20 px-6 bg-white">
            <div className="container mx-auto max-w-5xl">
              <h2 className="text-4xl md:text-5xl font-heading font-black text-center mb-16 text-slate-900">
                How We Take Care of Each Other
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center p-8 rounded-3xl bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Users className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-heading font-bold mb-3 text-slate-900">We Pool Together</h3>
                  <p className="text-slate-600">
                    Everyone chips in a small monthly amount. That's how we build the safety net that catches us all.
                  </p>
                </div>

                <div className="text-center p-8 rounded-3xl bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Heart className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-heading font-bold mb-3 text-slate-900">We Cover Bills</h3>
                  <p className="text-slate-600">
                    When you're short on rent, utilities, or essentials, we step in. Real people reviewing real needs.
                  </p>
                </div>

                <div className="text-center p-8 rounded-3xl bg-gradient-to-br from-orange-50 to-red-50 border-2 border-orange-200">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-600 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Shield className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-heading font-bold mb-3 text-slate-900">We Stay Protected</h3>
                  <p className="text-slate-600">
                    No late fees. No stress. Just peace of mind knowing your community has your back.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Social Proof / Testimonials */}
          <section className="py-20 px-6 bg-gradient-to-br from-purple-50 to-pink-50">
            <div className="container mx-auto max-w-4xl text-center">
              <h2 className="text-4xl md:text-5xl font-heading font-black mb-12 text-slate-900">
                Real People. Real Impact.
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  {
                    quote: "DueDay covered my electric bill when I got laid off. I didn't lose power. I didn't lose hope.",
                    name: "Sarah M.",
                    role: "Member since 2024",
                  },
                  {
                    quote: "It's not charity. It's community. We all lift each other up when we need it most.",
                    name: "James T.",
                    role: "Member since 2023",
                  },
                  {
                    quote: "Finally, a safety net that actually catches you. No banks. No predatory loans. Just people.",
                    name: "Maria R.",
                    role: "Member since 2024",
                  },
                ].map((testimonial, idx) => (
                  <div key={idx} className="bg-white p-6 rounded-2xl shadow-soft">
                    <p className="text-slate-700 mb-4 italic">"{testimonial.quote}"</p>
                    <div className="font-bold text-slate-900">{testimonial.name}</div>
                    <div className="text-sm text-slate-500">{testimonial.role}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Join Section */}
          <section id="join" className="py-24 px-6">
            <div className="container mx-auto max-w-6xl">
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-heading font-black mb-6 text-slate-900">
                  Choose Your Level of Protection
                </h2>
                <p className="text-xl text-slate-600">
                  Every tier helps build the movement. Pick what works for you.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {tiers.map((tier) => (
                  <div
                    key={tier.id}
                    className={`relative p-8 rounded-3xl border-2 transition-all ${
                      tier.featured
                        ? "border-purple-400 bg-gradient-to-br from-purple-50 to-pink-50 scale-105 shadow-xl"
                        : "border-slate-200 bg-white hover:border-purple-300"
                    }`}
                    data-testid={`tier-${tier.id}`}
                  >
                    {tier.featured && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-1 rounded-full text-sm font-bold">
                        Most Popular
                      </div>
                    )}

                    <div className="text-center mb-6">
                      <h3 className="text-2xl font-heading font-bold mb-2 text-slate-900">{tier.name}</h3>
                      <div className="text-5xl font-heading font-black mb-2">
                        <span className={`bg-gradient-to-r ${tier.color} bg-clip-text text-transparent`}>
                          ${tier.price}
                        </span>
                        <span className="text-xl text-slate-500">/mo</span>
                      </div>
                      <p className="text-sm text-slate-600">{tier.coverage}</p>
                    </div>

                    <div className="space-y-3 mb-8">
                      {["Human review within 24hrs", "Flexible payback terms", "Community support", "Cancel anytime"].map(
                        (feature, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <CheckCircle2 className="h-5 w-5 text-purple-600 flex-shrink-0" />
                            <span className="text-slate-700">{feature}</span>
                          </div>
                        )
                      )}
                    </div>

                    <Button
                      onClick={() => handleJoinMovement(tier)}
                      className={`w-full rounded-full py-6 text-lg font-bold ${
                        tier.featured
                          ? "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                          : "bg-slate-900 hover:bg-slate-800 text-white"
                      }`}
                      data-testid={`join-${tier.id}-btn`}
                    >
                      Join {tier.name}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Final CTA */}
          <section className="py-32 px-6 bg-gradient-to-br from-purple-600 to-pink-600 text-white">
            <div className="container mx-auto max-w-4xl text-center">
              <h2 className="text-5xl md:text-6xl font-heading font-black mb-6">
                Together, We're Unstoppable.
              </h2>
              <p className="text-2xl mb-12 opacity-90">
                No one should choose between rent and food. Join the movement that's changing how we take care of each
                other.
              </p>
              <Button
                onClick={() => document.getElementById("join").scrollIntoView({ behavior: "smooth" })}
                className="bg-white text-purple-600 hover:bg-slate-100 rounded-full px-12 py-7 text-2xl font-black shadow-2xl"
                data-testid="footer-cta-btn"
              >
                Join DueDay Now
              </Button>
            </div>
          </section>
        </>
      ) : (
        /* Signup Section with Embedded Payment */
        <section className="min-h-screen pt-24 pb-12 px-6">
          <div className="container mx-auto max-w-md">
            <button
              onClick={() => {
                setShowSignup(false);
                setClientSecret(null);
              }}
              className="mb-6 text-slate-600 hover:text-slate-900 flex items-center gap-2"
              data-testid="back-btn"
            >
              ← Back
            </button>

            <div className="bg-white rounded-3xl shadow-2xl p-8 border-2 border-purple-200">
              <div className="text-center mb-8">
                <div
                  className={`w-16 h-16 bg-gradient-to-br ${selectedTier?.color} rounded-2xl flex items-center justify-center mx-auto mb-4`}
                >
                  <Heart className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-3xl font-heading font-bold mb-2">Join {selectedTier?.name}</h2>
                <p className="text-slate-600">
                  ${selectedTier?.price}/month - {selectedTier?.coverage}
                </p>
              </div>

              {!clientSecret ? (
                <form onSubmit={handleFormSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      data-testid="signup-name-input"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="input-default mt-2"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      data-testid="signup-email-input"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="input-default mt-2"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      data-testid="signup-password-input"
                      type="password"
                      placeholder="Min 6 characters"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="input-default mt-2"
                      minLength={6}
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-full py-6 text-xl font-bold"
                    data-testid="continue-to-payment-btn"
                  >
                    {loading ? "Loading..." : "Continue to Payment"}
                  </Button>
                </form>
              ) : (
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                  <CheckoutForm tier={selectedTier} formData={formData} onSuccess={handlePaymentSuccess} />
                </Elements>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Landing_v3;
